import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "antd";
import Button from "../button";
import Icon from "../../assets/icons";
import { showToast } from "../toast";
import { RingLoader } from "react-spinners";
import useApi from "../hooks/request";
import FIlterSelect from "../filter-select";
import StepIndicator from "./indicator";
import OrderDetails from "./order-details";
import { I_Customer } from "../../pages/customers/types";
import AddProducts from "./add-products";
import { DeliveryInformation } from "./delivery-information";
import OrderSummary from "./order-summary";


interface AddOrderDialogProps {
  isVisible: boolean;
  onClose: () => void;
  customers?: I_Customer[];
  product?: any;
}

type FormStep = "orderDetails" | "products" | "information" | "summary";

const AddOrderDialog: React.FC<AddOrderDialogProps> = ({
  isVisible,
  onClose,
  customers = [],
  product,
}) => {
  const [currentStep, setCurrentStep] = useState<FormStep>("orderDetails");
  const [selectedCustomer, setSelectedCustomer] = useState<I_Customer | null>(
    null
  );
  // Modified initial state to better match the expected data structure
  const [orderProducts, setOrderProducts] = useState([
    { id: Date.now(), name: "", price: 0, _id: "", quantity: 0 },
  ]);
  // Add state for delivery information
  const [deliveryInfo, setDeliveryInfo] = useState({
    deliveryMethod: "",
    deliveryFee: 0,
    deliveryNote: ""
  });

  const {
    handleSubmit,
    trigger,
    control,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  // Watch delivery information fields
  React.useEffect(() => {
    const subscription = watch((value) => {
      setDeliveryInfo({
        deliveryMethod: value.deliveryMethod || "",
        deliveryFee: value.deliveryFee || 0,
        deliveryNote: value.deliveryNote || ""
      });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const { loading, makeRequest } = useApi("/orders/manual", "POST");

  // Handle customer selection from dropdown
  const handleCustomerSelection = (customerId: string) => {
    const customer = customers.find((c) => c._id === customerId);

    if (customer) {
      setSelectedCustomer(customer);
    }
  };

  // Create customer options for dropdown
  const customerOptions = customers.map((customer) => ({
    value: customer._id,
    label: customer.contactName,
  }));

  // Function to update product data from the AddProducts component
  const updateOrderProducts = (products: any[]) => {
    setOrderProducts(products);
  };

  const handleCreateOrder = handleSubmit(async (formData) => {
    if (!selectedCustomer) {
      showToast("Please select a customer", false, {
        position: "top-right",
      });
      return;
    }

    // Filter out any products that don't have an _id (weren't properly selected)
    const validProducts = orderProducts.filter((product) => product._id);

    if (validProducts.length === 0) {
      showToast("Please add at least one product to the order", false, {
        position: "top-right",
      });
      return;
    }

    const orderData = {
      customerId: selectedCustomer._id,
      // Modified to match the expected format from the JSON sample
      Items: validProducts.map((product) => ({
        _id: product._id,
        quantity: product.quantity,
        // No need to include name or price in the request since the backend
        // can look these up by _id
      })),
      discountCode: formData.discountCode,
      // Group delivery info under a delivery object to match the JSON example
      delivery: {
        deliveryFee: formData.deliveryFee,
        deliveryMethod: formData.deliveryMethod,
        deliveryNote: formData.deliveryNote,
      },
      source: "manual", // Added from the JSON example
    };

    const [response] = await makeRequest(orderData);

    if (response.status) {
      showToast(response.message || "Order created successfully", true, {
        position: "top-right",
      });
      onClose();
      reset();
      window.location.reload();
      setOrderProducts([
        { id: Date.now(), name: "", price: 0, _id: "", quantity: 0 },
      ]);
      setSelectedCustomer(null);
    } else {
      showToast(response?.message || "Failed to create order", false, {
        position: "top-right",
      });
    }
  });

  const validateAndProceed = async () => {
    let isValid = true;

    if (currentStep === "orderDetails") {
      // Check if customer is selected before proceeding
      if (!selectedCustomer) {
        showToast("Please select a customer", false);
        return;
      }

      const fieldsToValidate = [
        "date",
        "businessType",
        "accountType",
        "paymentTerm",
        "paymentPeriod",
        "availableCredit",
        "paymentDueDay",
        "paymentStatus",
      ];
      isValid = await trigger(fieldsToValidate);
    } else if (currentStep === "products") {
      // Validate products have been selected
      const validProducts = orderProducts.filter(
        (product) => product._id && product.quantity > 0
      );

      if (validProducts.length === 0) {
        showToast("Please select at least one product with quantity", false);
        isValid = false;
      }
    } else if (currentStep === "information") {
      const fieldsToValidate = ["deliveryMethod", "deliveryFee"];
      isValid = await trigger(fieldsToValidate);
      if (isValid) {
        // Move to summary step instead of submitting
        setCurrentStep("summary");
        return;
      }
    } else if (currentStep === "summary") {
      // Submit the form
      handleCreateOrder();
      return;
    }

    if (isValid) {
      if (currentStep === "orderDetails") {
        setCurrentStep("products");
      } else if (currentStep === "products") {
        setCurrentStep("information");
      }
    }
  };

  const goBackToPreviousStep = () => {
    if (currentStep === "products") {
      setCurrentStep("orderDetails");
    } else if (currentStep === "information") {
      setCurrentStep("products");
    } else if (currentStep === "summary") {
      setCurrentStep("information");
    }
  };

  const renderCustomerSelection = () => {
    return (
      <div className="mb-6">
        <FIlterSelect
          name="customerId"
          options={customerOptions}
          className="w-full"
          label="Select Customer"
          onChange={(selectedValue) => {
            handleCustomerSelection(selectedValue);
          }}
          value={selectedCustomer?._id || ""}
        />
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "orderDetails":
        return (
          <>
            {renderCustomerSelection()}
            <OrderDetails
              control={control}
              errors={errors}
              customer={selectedCustomer}
            />
          </>
        );
      case "products":
        return (
          <div>
            <AddProducts
              product={product}
              control={control}
              setValue={setValue}
              initialProducts={orderProducts}
              onProductsChange={updateOrderProducts}
            />
          </div>
        );
      case "information":
        return (
          <div>
            <DeliveryInformation control={control} errors={errors} />
          </div>
        );
      case "summary":
        return (
          <div>
            <OrderSummary 
              customer={selectedCustomer}
              products={orderProducts.filter(p => p._id)}
              deliveryInfo={deliveryInfo}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const renderFooterButtons = () => {
    if (currentStep === "orderDetails") {
      return (
        <div className="flex gap-3 py-2 mt-4">
          <Button
            type="button"
            className="hover:bg-green-700 flex items-center"
            onClick={validateAndProceed}
            variant="primary"
            size="lg"
          >
            <Icon name="noteIcon" />
            Proceed to Add Product
          </Button>
        </div>
      );
    } else if (currentStep === "products") {
      return (
        <div className="flex gap-2 items-center text-center py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 px-7 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            Back to Order Details
          </button>
          <Button
            type="button"
            size="lg"
            variant="primary"
            className="hover:bg-green-700 text-white flex"
            onClick={validateAndProceed}
          >
            <Icon name="noteIcon" />
            Proceed to Delivery Information
          </Button>
        </div>
      );
    } else if (currentStep === "information") {
      return (
        <div className="flex gap-2 items-center text-center py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 px-7 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            Back to Add Products
          </button>
          <Button
            type="button"
            size="lg"
            variant="primary"
            className="hover:bg-green-700 text-white flex"
            onClick={validateAndProceed}
          >
            <Icon name="noteIcon" />
            Continue to Summary
          </Button>
        </div>
      );
    } else if (currentStep === "summary") {
      return (
        <div className="flex justify-between gap-2 py-2 mt-4">
          <button
            type="button"
            className="bg-[#E8F2E8] w-full rounded-full hover:bg-gray-200 px-7 py-2 text-[#047401] flex font-medium"
            onClick={goBackToPreviousStep}
          >
            <Icon name="backIcon" />
            Edit Order
          </button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="hover:bg-green-700 text-white flex items-center"
            onClick={validateAndProceed}
          >
            {loading ? (
              <RingLoader color="#ffffff" loading={loading} size={20} />
            ) : (
              <div className="flex gap-3">
                Submit For Approval <Icon name="noteIcon" />
              </div>
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal
      visible={isVisible}
      onCancel={onClose}
      width="80%"
        centered={true}
        footer={null}
        style={{
          padding: 0,
          margin: 0,
          maxWidth: "1200px",
          height: "auto",
        }}

        
  className="fullscreen-modal"
    >
      <h1 className="text-2xl font-bold -mt-2 mb-4">New Sales Order</h1>
      <StepIndicator
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
      />
      <form onSubmit={handleCreateOrder}>
        <div className="grid gap-4">{renderStepContent()}</div>
        {renderFooterButtons()}
      </form>
    </Modal>
  );
};

export default AddOrderDialog;